import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPayment,
  applyDepositToPayment,
  fetchInvoicePaymentsByAppointmentId,
} from "../store/thunks/paymentsThunk";
import { fetchAppointmentById } from "../store/thunks/appointmentsThunk";
import {
  fetchClinicPaymentMethods,
  fetchClinicById,
} from "../store/thunks/clinicThunk";
import { fetchDeposits } from "../store/thunks/paymentsThunk";
import { fetchClientById } from "../store/thunks/clientsThunk";
import CurrencyInput from "react-currency-input-field";

const InvoicePaymentModal = ({ payment, onClose }) => {
  const invoicesFromStore = useSelector(
    (state) => state.invoices.appointmentInvoices
  );
  const payments = useSelector((state) => state.payments.appointmentPayments);
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);
  const paymentStatus = useSelector((state) => state.payments.status);
  // const paymentMethods = Array.isArray(selectedClinic?.paymentMethods)
  //   ? selectedClinic.paymentMethods
  //   : [];
  const user = useSelector((state) => state.auth.user);
  const selectedAppointment = useSelector((state) => state.appointments.selectedAppointment);
  const depositsFromRedux = useSelector((state) => state.payments.deposits || []);
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false);
  const [familyMembers, setFamilyMembers] = useState({});

  // Normalize invoices
  const invoiceArray = Array.isArray(invoicesFromStore)
    ? invoicesFromStore
    : invoicesFromStore
    ? [invoicesFromStore]
    : [];

  const dispatch = useDispatch();

  useEffect(() => {
    if (invoiceArray.length > 0 && invoiceArray[0]?.clinicId) {
      dispatch(fetchClinicById(invoiceArray[0].clinicId));
    }
  }, [invoiceArray, dispatch]);

  // console.log("Payment Methods in Dropdown:", paymentMethods);
  const [paymentMethods, setPaymentMethods] = useState([]); // Local state for payment methods
  const [paymentMethod, setPaymentMethod] = useState(
    payment?.paymentMethod || ""
  );
  const [selectedInvoices, setSelectedInvoices] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [selectedDeposits, setSelectedDeposits] = useState({});
  const [depositAmount, setDepositAmount] = useState(0);
  // const [paymentMethod, setPaymentMethod] = useState(
  //   payment?.paymentMethod || ""
  // );

  const [paymentDate, setPaymentDate] = useState(
    new Date(payment?.paymentDate || new Date()).toISOString().split("T")[0]
  );
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Use a ref to track if we've already initialized the state
  const initializedRef = useRef(false);

  // Add these new state variables at the top with other state declarations
  const [selectedDepositAmounts, setSelectedDepositAmounts] = useState({});

  // Filter deposits from Redux state by clinic and family membership
  const deposits = useMemo(() => {
    if (!depositsFromRedux.length || !invoiceArray.length) return [];

    const firstInvoice = invoiceArray[0];
    const invoiceClinicId = firstInvoice?.clinicId || firstInvoice?.clinic?._id;

    // Get family member IDs from the familyMembers state
    const familyMemberIds = Object.keys(familyMembers);

    console.log(`[Invoice Payment Modal] Filtering deposits:`, {
      depositsCount: depositsFromRedux.length,
      invoiceClinicId,
      familyMemberIds,
      deposits: depositsFromRedux.map(d => ({
        _id: d._id,
        clientId: d.clientId,
        clinic: d.clinic,
        amount: d.amount,
        balance: d.depositBalance
      }))
    });

    const filtered = depositsFromRedux.filter(deposit => {
      // First check if deposit belongs to a family member
      const belongsToFamily = deposit.clientId && familyMemberIds.includes(deposit.clientId);
      if (!belongsToFamily) {
        console.log(`[Invoice Payment Modal] Deposit ${deposit._id} filtered out - not a family member (clientId: ${deposit.clientId})`);
        return false;
      }

      // Then check clinic matching
      let depositClinicId = null;

      if (deposit.clinic && deposit.clinic !== null) {
        if (typeof deposit.clinic === 'object' && deposit.clinic._id) {
          depositClinicId = deposit.clinic._id;
        } else if (typeof deposit.clinic === 'string') {
          depositClinicId = deposit.clinic;
        }
      }

      // Allow null-clinic deposits if invoice has no specific clinic ID
      if (!depositClinicId) {
        const result = !invoiceClinicId;
        console.log(`[Invoice Payment Modal] Deposit ${deposit._id} clinic match (null clinic): ${result}`);
        return result;
      }

      // Compare clinic IDs
      const clinicMatch = depositClinicId === invoiceClinicId ||
             depositClinicId.toString() === invoiceClinicId.toString();

      console.log(`[Invoice Payment Modal] Deposit ${deposit._id} clinic match: ${clinicMatch} (${depositClinicId} vs ${invoiceClinicId})`);
      return clinicMatch;
    });

    console.log(`[Invoice Payment Modal] Filtered deposits result:`, filtered.length, filtered);
    return filtered;
  }, [depositsFromRedux, invoiceArray, familyMembers]);


  // Calculate outstanding balance for each invoice
  const calculateInvoiceDetails = (invoice) => {
    const totalPaid = payments?.reduce((sum, pay) => {
      if (pay.voided) return sum;
      
      // For deposit payments, check if invoice is in the depositUsage
      if (pay.deposit && pay.depositUsage && Array.isArray(pay.depositUsage)) {
        const depositUsageForInvoice = pay.depositUsage
          .filter(usage => usage.invoiceId === invoice._id)
          .reduce((depSum, usage) => depSum + (usage.amountUsed || 0), 0);
        return sum + depositUsageForInvoice;
      }
      
      // For regular payments, check if invoiceId matches (single invoice or array)
      let isRelatedToInvoice = false;
      if (Array.isArray(pay.invoiceId)) {
        isRelatedToInvoice = pay.invoiceId.some(inv => inv._id === invoice._id);
      } else if (pay.invoiceId?._id) {
        isRelatedToInvoice = pay.invoiceId._id === invoice._id;
      }
      
      if (isRelatedToInvoice) {
        return sum + (pay.amount || 0);
      }
      
      return sum;
    }, 0) || 0;

    const outstandingBalance = Math.max(0, (invoice.amount || 0) - totalPaid);
    
    // Special handling for $0 invoices - they're only considered paid if they have a payment record
    const isFullyPaid = invoice.amount === 0 
      ? payments?.some(pay => !pay.voided && (
          (Array.isArray(pay.invoiceId) && pay.invoiceId.some(inv => inv._id === invoice._id)) ||
          (!Array.isArray(pay.invoiceId) && pay.invoiceId?._id === invoice._id)
        ))
      : totalPaid >= (invoice.amount || 0);
      
    const isPartiallyPaid = totalPaid > 0 && totalPaid < (invoice.amount || 0);

    return {
      totalPaid,
      outstandingBalance,
      isFullyPaid,
      isPartiallyPaid
    };
  };

  // Fetch deposits for the client and family members (parents/children)
  useEffect(() => {
    const getFamilyDeposits = async () => {
      if (invoiceArray.length === 0) return;

      const firstInvoice = invoiceArray[0];
      
      // Handle both populated client object and ObjectId reference
      let clientId = null;
      if (firstInvoice?.client) {
        if (typeof firstInvoice.client === 'object' && firstInvoice.client._id) {
          clientId = firstInvoice.client._id;
        } else if (typeof firstInvoice.client === 'string') {
          clientId = firstInvoice.client;
        }
      }
      
      if (!clientId) {
        console.log(`[Invoice Payment Modal] No client ID found in invoice:`, firstInvoice);
        return;
      }

      console.log(`[Invoice Payment Modal] Fetching deposits for client and family members for client ID: ${clientId}`);
      setIsLoadingDeposits(true);
      
      try {
        // First, fetch the client data to get family relationships
        console.log(`[Invoice Payment Modal] Fetching client data for ${clientId}`);
        const clientData = await dispatch(fetchClientById(clientId)).unwrap();
        console.log(`[Invoice Payment Modal] Client data:`, clientData);
        
        // Collect all family member IDs (client + parents + children) and their data
        const familyMemberIds = [clientId];
        const familyMembersMap = {
          [clientId]: {
            _id: clientId,
            firstName: clientData.firstName,
            lastName: clientData.lastName,
            relationship: 'self'
          }
        };
        
        // Add parent IDs if they exist
        if (clientData.parents && Array.isArray(clientData.parents)) {
          for (const parent of clientData.parents) {
            const parentId = typeof parent === 'object' ? parent._id : parent;
            if (parentId && !familyMemberIds.includes(parentId)) {
              familyMemberIds.push(parentId);
              
              // Fetch parent data to get name
              try {
                const parentData = await dispatch(fetchClientById(parentId)).unwrap();
                familyMembersMap[parentId] = {
                  _id: parentId,
                  firstName: parentData.firstName,
                  lastName: parentData.lastName,
                  relationship: 'parent'
                };
              } catch (error) {
                console.warn(`[Invoice Payment Modal] Failed to fetch parent data for ${parentId}:`, error);
                familyMembersMap[parentId] = {
                  _id: parentId,
                  firstName: 'Unknown',
                  lastName: 'Parent',
                  relationship: 'parent'
                };
              }
            }
          }
        }
        
        // Add children IDs if they exist
        if (clientData.children && Array.isArray(clientData.children)) {
          for (const child of clientData.children) {
            const childId = typeof child === 'object' ? child._id : child;
            if (childId && !familyMemberIds.includes(childId)) {
              familyMemberIds.push(childId);
              
              // Fetch child data to get name
              try {
                const childData = await dispatch(fetchClientById(childId)).unwrap();
                familyMembersMap[childId] = {
                  _id: childId,
                  firstName: childData.firstName,
                  lastName: childData.lastName,
                  relationship: 'child'
                };
              } catch (error) {
                console.warn(`[Invoice Payment Modal] Failed to fetch child data for ${childId}:`, error);
                familyMembersMap[childId] = {
                  _id: childId,
                  firstName: 'Unknown',
                  lastName: 'Child',
                  relationship: 'child'
                };
              }
            }
          }
        }
        
        // Store family members data for display
        setFamilyMembers(familyMembersMap);
        
        console.log(`[Invoice Payment Modal] Fetching deposits for family members:`, familyMemberIds);
        
        // Fetch deposits for all family members
        const depositPromises = familyMemberIds.map(memberId => 
          dispatch(fetchDeposits(memberId)).unwrap().catch(error => {
            console.warn(`[Invoice Payment Modal] Failed to fetch deposits for family member ${memberId}:`, error);
            return []; // Return empty array if individual fetch fails
          })
        );
        
        const allDepositsResults = await Promise.all(depositPromises);
        console.log(`[Invoice Payment Modal] Successfully fetched deposits for all family members`);
        
        // Note: The deposits are now stored in Redux state and will be filtered by the deposits useMemo
        
      } catch (error) {
        console.error(`[Invoice Payment Modal] Failed to fetch family deposits for client ${clientId}:`, error);
      } finally {
        setIsLoadingDeposits(false);
      }
    };

    getFamilyDeposits();
  }, [invoiceArray, dispatch]);

  useEffect(() => {
    // If we've already initialized once and payment is not changing, skip re-initialization
    if (initializedRef.current && !payment) return;

    if (payment) {
      // When `payment` is passed, initialize with its details only once
      const invoice = payment.invoiceId;
      if (invoice && invoice._id) {
        const invoiceDetails = calculateInvoiceDetails(invoice);
        setSelectedInvoices({ [invoice._id]: true });
        setPaymentAmounts({ [invoice._id]: invoiceDetails.outstandingBalance.toFixed(2) });
      } else {
        console.warn("Invalid payment invoiceId:", invoice);
      }
      initializedRef.current = true;
    } else {
      // If no payment, initialize the selections only once when invoices are available
      if (invoiceArray.length > 0 && !initializedRef.current) {
        const initialSelection = {};
        const initialAmounts = {};

        invoiceArray.forEach((invoice) => {
          if (!invoice || !invoice._id) {
            console.warn("Invalid invoice object:", invoice);
            return;
          }

          const invoiceDetails = calculateInvoiceDetails(invoice);
          initialSelection[invoice._id] = false;
          initialAmounts[invoice._id] = invoiceDetails.outstandingBalance.toFixed(2);
        });

        setSelectedInvoices(initialSelection);
        setPaymentAmounts(initialAmounts);
        initializedRef.current = true; // Mark as initialized
      }
    }
  }, [payment, invoiceArray, payments]);

  // Fetch payment methods based on the first invoice's clinic
  useEffect(() => {
    const fetchMethods = async () => {
      if (invoiceArray.length === 0) return;

      const firstInvoice = invoiceArray[0];
      const invoiceClinicId = firstInvoice?.clinicId || firstInvoice?.clinic?._id;
      
      console.log("[Invoice Payment Modal] First invoice:", firstInvoice);
      console.log("[Invoice Payment Modal] Extracted clinic ID:", invoiceClinicId);
      
      if (!firstInvoice || !invoiceClinicId) {
        console.log("[Invoice Payment Modal] Missing invoice or clinic ID, skipping payment methods fetch");
        return;
      }

      try {
        console.log("[Invoice Payment Modal] Fetching payment methods for clinic:", invoiceClinicId);
        const paymentMethodsData = await dispatch(
          fetchClinicPaymentMethods(invoiceClinicId)
        ).unwrap();
        console.log("[Invoice Payment Modal] Successfully fetched payment methods:", paymentMethodsData);
        setPaymentMethods(paymentMethodsData);
      } catch (error) {
        console.error("[Invoice Payment Modal] Failed to fetch payment methods:", error);
        // Fallback to clinic payment methods if the fetch fails
        if (selectedClinic?.paymentMethods) {
          console.log("[Invoice Payment Modal] Using fallback payment methods from selectedClinic:", selectedClinic.paymentMethods);
          setPaymentMethods(selectedClinic.paymentMethods);
        }
      }
    };

    // Also try to set payment methods from selectedClinic immediately
    if (selectedClinic?.paymentMethods) {
      console.log("[Invoice Payment Modal] Setting payment methods from selectedClinic:", selectedClinic.paymentMethods);
      setPaymentMethods(selectedClinic.paymentMethods);
    }

    fetchMethods();
  }, [invoiceArray, dispatch, selectedClinic]);

  const handleInvoiceSelection = (invoiceId) => {
    if (payment) return; // Disable selection changes when editing a passed payment
    
    const invoice = invoiceArray.find(inv => inv._id === invoiceId);
    if (!invoice) return;
    
    const invoiceDetails = calculateInvoiceDetails(invoice);
    
    // Allow selection if invoice is not fully paid (including $0 invoices)
    if (!invoiceDetails.isFullyPaid) {
      setSelectedInvoices((prev) => ({
        ...prev,
        [invoiceId]: !prev[invoiceId],
      }));

      // For $0 invoices, set payment amount to 0
      if (invoice.amount === 0) {
        setPaymentAmounts((prev) => ({
          ...prev,
          [invoiceId]: "0.00",
        }));
      }
    }
  };

  const handlePaymentAmountChange = (invoiceId, value) => {
    const invoice = invoiceArray.find(inv => inv._id === invoiceId);
    if (invoice) {
      const invoiceDetails = calculateInvoiceDetails(invoice);
      const numericValue = parseFloat(value || "0");
      
      // Limit the payment amount to the outstanding balance
      const limitedValue = Math.min(numericValue, invoiceDetails.outstandingBalance);
      
      setPaymentAmounts((prev) => ({
        ...prev,
        [invoiceId]: limitedValue.toFixed(2),
      }));
    } else {
      setPaymentAmounts((prev) => ({
        ...prev,
        [invoiceId]: value || "0.00",
      }));
    }
  };

  const totalPaymentAmount = payment
    ? parseFloat(paymentAmounts[payment.invoiceId?._id] || "0")
    : invoiceArray
        .filter(
          (invoice) => invoice && invoice._id && selectedInvoices[invoice._id]
        )
        .reduce(
          (total, invoice) =>
            total + parseFloat(paymentAmounts[invoice._id] || "0"),
          0
        );

  const calculateTotalSelectedDeposits = () => {
    return Object.entries(selectedDeposits)
      .filter(([_, isSelected]) => isSelected)
      .reduce((total, [depositId]) => {
        const deposit = deposits.find((d) => d._id === depositId);
        return total + (deposit?.depositBalance || 0);
      }, 0);
  };

  // Add this helper function
  const getMaxDepositAmount = (depositId) => {
    const deposit = deposits.find((d) => d._id === depositId);
    return deposit ? deposit.depositBalance : 0;
  };

  // Add this helper function near your other helper functions
  const calculateTotalSelectedDepositAmounts = () => {
    return Object.values(selectedDepositAmounts).reduce(
      (sum, amount) => sum + (parseFloat(amount) || 0),
      0
    );
  };

  // Helper function to get deposit owner information
  const getDepositOwnerInfo = (deposit) => {
    const ownerId = deposit.clientId;
    const owner = familyMembers[ownerId];

    if (owner) {
      const name = `${owner.firstName} ${owner.lastName}`;
      const relationship = owner.relationship === 'self' ? '' : ` (${owner.relationship})`;
      return `${name}${relationship}`;
    }

    return 'Unknown Owner';
  };

  // Update the handleDepositAmountChange function
  const handleDepositAmountChange = (depositId, value) => {
    setSelectedDepositAmounts((prev) => ({
      ...prev,
      [depositId]: value || "0.00",
    }));
  };

  const handleSavePayment = async () => {
    // Check if any selected invoice has a non-zero amount
    const hasNonZeroInvoice = invoiceArray.some(
      (invoice) =>
        invoice &&
        invoice._id &&
        selectedInvoices[invoice._id] &&
        invoice.amount > 0
    );

    // Validate payment amounts don't exceed outstanding balances
    const invalidPayments = invoiceArray.filter(invoice => {
      if (!invoice || !invoice._id || !selectedInvoices[invoice._id]) return false;
      
      const invoiceDetails = calculateInvoiceDetails(invoice);
      const paymentAmount = parseFloat(paymentAmounts[invoice._id] || "0");
      
      // Use tolerance-based comparison to handle floating point precision issues
      return paymentAmount > invoiceDetails.outstandingBalance + 0.01;
    });

    if (invalidPayments.length > 0) {
      setError("Payment amount cannot exceed the outstanding balance for selected invoices.");
      return;
    }

    // If there are non-zero invoices but no payment method or deposits selected, show error
    if (
      hasNonZeroInvoice &&
      Object.keys(selectedDeposits).length === 0 &&
      (!paymentMethod || totalPaymentAmount <= 0)
    ) {
      setError(
        "Please select unpaid invoices and enter a payment method or select deposits."
      );
      return;
    }

    // Only show error for $0 payment if the invoice amount is greater than $0
    const hasZeroPaymentForNonZeroInvoice = invoiceArray.some(invoice => 
      invoice && 
      invoice._id && 
      selectedInvoices[invoice._id] && 
      invoice.amount > 0 && 
      parseFloat(paymentAmounts[invoice._id] || "0") === 0
    );

    if (hasZeroPaymentForNonZeroInvoice) {
      setError("Payment amount cannot be $0 for non-zero invoices.");
      return;
    }

    const totalSelectedDeposits = calculateTotalSelectedDepositAmounts();
    if (totalSelectedDeposits > totalPaymentAmount) {
      setError(
        "Selected deposit amounts exceed the invoice total. Please adjust the amounts."
      );
      return;
    }

    if (
      Object.keys(selectedDeposits).length > 0 &&
      totalSelectedDeposits < totalPaymentAmount
    ) {
      setError(
        "Selected deposit amounts don't cover the full invoice amount. Please adjust the amounts or select additional deposits."
      );
      return;
    }

    setError("");

    try {
      if (payment) {
        // Editing an existing payment
        const invoice = payment.invoiceId;
        if (invoice && invoice._id) {
          if (Object.keys(selectedDeposits).length > 0) {
            // Handle multiple deposits
            let remainingPaymentAmount = parseFloat(
              paymentAmounts[invoice._id]
            );
            const depositsToUse = [];
            const depositAmounts = [];

            for (const [depositId, isSelected] of Object.entries(
              selectedDeposits
            )) {
              if (!isSelected || remainingPaymentAmount <= 0) continue;

              const amountToUse = Math.min(
                selectedDepositAmounts[depositId] || 0,
                remainingPaymentAmount
              );

              if (amountToUse <= 0) continue;

              depositsToUse.push(depositId);
              depositAmounts.push(amountToUse);
              remainingPaymentAmount -= amountToUse;
            }

            // If there's still remaining amount, show error
            if (remainingPaymentAmount > 0) {
              throw new Error("Insufficient deposit balance for full payment");
            }

            // Send all deposits in a single request
            await dispatch(
              applyDepositToPayment({
                depositIds: depositsToUse,
                amounts: depositAmounts,
                invoiceId: invoice._id,
                invoiceNumber: invoice.invoiceId,
                userId: user._id,
              })
            ).unwrap();
            
            // Refresh payments and appointment after successful deposit application
            if (selectedAppointment?._id) {
              try {
                await Promise.all([
                  dispatch(fetchInvoicePaymentsByAppointmentId(selectedAppointment._id)).unwrap(),
                  dispatch(fetchAppointmentById(selectedAppointment._id)).unwrap()
                ]);
              } catch (refreshError) {
                console.error(`[Invoice Payment Modal] Error refreshing data:`, refreshError);
                // Don't throw - the payment was successful, just log the refresh error
              }
            }
          }

          if (paymentStatus === "succeeded") {
            setSuccessMessage("Payment created successfully!");
          }
        } else {
          setError("Invalid invoice data for this payment.");
        }
      } else {
        // Processing new payments
        let successCount = 0;
        let remainingAmount = totalPaymentAmount;

        for (const invoice of invoiceArray) {
          if (invoice && invoice._id && selectedInvoices[invoice._id]) {
            try {
              const paymentAmount = parseFloat(paymentAmounts[invoice._id]);

              if (Object.keys(selectedDeposits).length > 0) {
                // Handle multiple deposits
                let remainingPaymentAmount = paymentAmount;
                const depositsToUse = [];
                const depositAmounts = [];

                for (const [depositId, isSelected] of Object.entries(
                  selectedDeposits
                )) {
                  if (!isSelected || remainingPaymentAmount <= 0) continue;

                  const amountToUse = Math.min(
                    selectedDepositAmounts[depositId] || 0,
                    remainingPaymentAmount
                  );

                  if (amountToUse <= 0) continue;

                  depositsToUse.push(depositId);
                  depositAmounts.push(amountToUse);
                  remainingPaymentAmount -= amountToUse;
                }

                // If there's still remaining amount, show error
                if (remainingPaymentAmount > 0) {
                  throw new Error(
                    "Insufficient deposit balance for full payment"
                  );
                }

                // Send all deposits in a single request
                await dispatch(
                  applyDepositToPayment({
                    depositIds: depositsToUse,
                    amounts: depositAmounts,
                    invoiceId: invoice._id,
                    invoiceNumber: invoice.invoiceId,
                    userId: user._id,
                  })
                ).unwrap();
                
                // Refresh payments and appointment after successful deposit application
                if (selectedAppointment?._id) {
                  try {
                    await Promise.all([
                      dispatch(fetchInvoicePaymentsByAppointmentId(selectedAppointment._id)).unwrap(),
                      dispatch(fetchAppointmentById(selectedAppointment._id)).unwrap()
                    ]);
                  } catch (refreshError) {
                    console.error(`[Invoice Payment Modal] Error refreshing data:`, refreshError);
                    // Don't throw - the payment was successful, just log the refresh error
                  }
                }
              } else {
                // Regular payment processing
                await dispatch(
                  createPayment({
                    invoiceId: invoice._id,
                    invoiceNumber: invoice.invoiceId,
                    paymentMethod,
                    paymentAmount: paymentAmount.toFixed(2),
                    paymentDate: new Date(paymentDate).toISOString(),
                    createdBy: user._id,
                  })
                ).unwrap();
                
                // Refresh payments and appointment after successful payment creation
                if (selectedAppointment?._id) {
                  try {
                    await Promise.all([
                      dispatch(fetchInvoicePaymentsByAppointmentId(selectedAppointment._id)).unwrap(),
                      dispatch(fetchAppointmentById(selectedAppointment._id)).unwrap()
                    ]);
                  } catch (refreshError) {
                    console.error(`[Invoice Payment Modal] Error refreshing data:`, refreshError);
                    // Don't throw - the payment was successful, just log the refresh error
                  }
                }
              }
              successCount++;
            } catch (err) {
              console.error(
                `Failed to process payment for invoice ${invoice._id}:`,
                err
              );
              setError(err.message || "Failed to process payment");
            }
          }
        }

        if (successCount === 0) {
          setError("Failed to process any payments. Please try again.");
        } else if (paymentStatus === "succeeded") {
          setSuccessMessage("Payments processed successfully!");
        }
      }
    } catch (error) {
      console.error("Error processing payments:", error);
      setError("Failed to process payments. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75 p-4">
      <div className="bg-white rounded-sm shadow-xl border border-gray-200 w-full max-w-7xl max-h-[90vh] flex flex-col relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-sm hover:bg-gray-100 z-10"
          aria-label="Close">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-green-600 flex items-center justify-center">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Invoice Payment</h2>
              <p className="text-sm text-gray-500 mt-0.5">Process payments for selected invoices</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Invoice Selection */}
          <div className="bg-white border border-gray-200 rounded-sm">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {payment ? "Invoice Details" : "Select Invoices to Pay"}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {!payment && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Select
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Invoice ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Service Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Client Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Invoice Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Total Paid
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Outstanding Balance
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Payment Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(payment ? [payment.invoiceId] : invoiceArray).map(
                    (invoice) => {
                      if (!invoice || !invoice._id) return null;
                      const invoiceDetails = calculateInvoiceDetails(invoice);

                      return (
                        <tr
                          key={invoice._id}
                          className={`hover:bg-gray-50 transition-colors ${invoiceDetails.isFullyPaid ? "bg-gray-50" : ""}`}>
                          {!payment && (
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={!!selectedInvoices[invoice._id]}
                                onChange={() =>
                                  handleInvoiceSelection(invoice._id)
                                }
                                disabled={invoiceDetails.isFullyPaid}
                                className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.invoiceId}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {invoice.serviceDate
                              ? new Date(invoice.serviceDate).toLocaleDateString()
                              : ""}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {invoice.client?.firstName} {invoice.client?.lastName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            ${invoice.amount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            ${invoiceDetails.totalPaid.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            <span className={invoiceDetails.outstandingBalance > 0 ? "text-red-600" : "text-green-600"}>
                              ${invoiceDetails.outstandingBalance.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <CurrencyInput
                              id={`payment-amount-${invoice._id}`}
                              name={`payment-amount-${invoice._id}`}
                              prefix="$"
                              placeholder="Enter amount"
                              value={paymentAmounts[invoice._id] || "0.00"}
                              onValueChange={(value) =>
                                handlePaymentAmountChange(invoice._id, value)
                              }
                              decimalsLimit={2}
                              decimalSeparator="."
                              intlConfig={{
                                locale: "en-US",
                                currency: "USD",
                              }}
                              max={invoiceDetails.outstandingBalance}
                              disabled={
                                (!payment && !selectedInvoices[invoice._id]) ||
                                invoiceDetails.isFullyPaid
                              }
                              className="w-28 rounded-sm border border-gray-300 shadow-sm px-3 py-1.5 text-sm focus:border-gray-900 focus:ring-gray-900"
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {invoiceDetails.isFullyPaid ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Paid
                              </span>
                            ) : invoiceDetails.isPartiallyPaid ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Partial Payment
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Unpaid
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Total Payment Amount</span>
              <span className="text-xl font-bold text-blue-900">${totalPaymentAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Deposits Section */}
          <div className="space-y-4">
            {/* Selected Deposits */}
            <div className="bg-white border border-gray-200 rounded-sm">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Selected Deposits</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Deposits from client and family members available for payment
                </p>
              </div>
              <div className="p-4">
                {Object.keys(selectedDeposits).filter((id) => selectedDeposits[id]).length > 0 ? (
                  <div className="space-y-4">
                    <div className="overflow-hidden rounded-sm border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Owner
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Available
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Amount to Use
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {deposits
                            .filter((deposit) => selectedDeposits[deposit._id])
                            .map((deposit) => (
                              <tr key={deposit._id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {new Date(deposit.paymentDate).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {getDepositOwnerInfo(deposit)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  ${deposit.depositBalance.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <CurrencyInput
                                    id={`deposit-amount-${deposit._id}`}
                                    name={`deposit-amount-${deposit._id}`}
                                    prefix="$"
                                    placeholder="Enter amount"
                                    value={selectedDepositAmounts[deposit._id] || "0.00"}
                                    onValueChange={(value) =>
                                      handleDepositAmountChange(
                                        deposit._id,
                                        value
                                      )
                                    }
                                    decimalsLimit={2}
                                    decimalSeparator="."
                                    intlConfig={{
                                      locale: "en-US",
                                      currency: "USD",
                                    }}
                                    max={deposit.depositBalance}
                                    className="w-28 rounded-sm border border-gray-300 shadow-sm px-3 py-1.5 text-sm focus:border-gray-900 focus:ring-gray-900"
                                  />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    onClick={() => {
                                      setSelectedDeposits((prev) => {
                                        const newDeposits = { ...prev };
                                        delete newDeposits[deposit._id];
                                        return newDeposits;
                                      });
                                      setSelectedDepositAmounts((prev) => {
                                        const newAmounts = { ...prev };
                                        delete newAmounts[deposit._id];
                                        return newAmounts;
                                      });
                                    }}
                                    className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors">
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-gray-50 rounded-sm border border-gray-200 p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">
                          Total Selected: ${calculateTotalSelectedDepositAmounts().toFixed(2)}
                        </span>
                        {calculateTotalSelectedDepositAmounts() > totalPaymentAmount && (
                          <span className="text-sm text-red-600 font-medium">
                            Selected deposits exceed invoice amount (${totalPaymentAmount.toFixed(2)})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No deposits selected</h3>
                    <p className="mt-1 text-sm text-gray-500">Select deposits from the available list below.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Available Deposits */}
            <div className="bg-white border border-gray-200 rounded-sm">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Available Deposits</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Deposits from client and family members for this clinic
                </p>
              </div>
              <div className="p-4">
                {isLoadingDeposits ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-pulse flex space-x-4">
                      <div className="rounded-sm bg-gray-200 h-12 w-12"></div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ) : deposits.filter((deposit) => !selectedDeposits[deposit._id] && deposit.depositBalance > 0).length > 0 ? (
                  <div className="max-h-[200px] overflow-auto rounded-sm border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Select
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Owner
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deposits
                          .filter((deposit) => !selectedDeposits[deposit._id] && deposit.depositBalance > 0)
                          .map((deposit) => (
                            <tr key={deposit._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => {
                                    setSelectedDeposits((prev) => ({
                                      ...prev,
                                      [deposit._id]: true,
                                    }));
                                    setSelectedDepositAmounts((prev) => ({
                                      ...prev,
                                      [deposit._id]: deposit.depositBalance.toFixed(2),
                                    }));
                                  }}
                                  className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {new Date(deposit.paymentDate).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {getDepositOwnerInfo(deposit)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                ${deposit.amount.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                ${deposit.depositBalance.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No available deposits</h3>
                    <p className="mt-1 text-sm text-gray-500">This client and their family members have no available deposit funds for this clinic.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Method and Date */}
          {Object.values(selectedDeposits).filter((isSelected) => isSelected).length === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="block w-full rounded-sm border border-gray-300 shadow-sm px-3 py-2 text-sm focus:border-gray-900 focus:ring-gray-900">
                  <option value="">Select Method</option>
                  {paymentMethods?.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.englishDescription}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-900">
                  Payment Date
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="block w-full rounded-sm border border-gray-300 shadow-sm px-3 py-2 text-sm focus:border-gray-900 focus:ring-gray-900"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          {successMessage && (
            <div className="mb-4 rounded-sm border border-green-200 bg-green-50 p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-700 font-medium">{successMessage}</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 rounded-sm border border-red-200 bg-red-50 p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSavePayment}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-sm shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              disabled={
                paymentStatus === "loading" ||
                successMessage ||
                (Object.keys(selectedDeposits).length > 0
                  ? Math.abs(
                      calculateTotalSelectedDepositAmounts() -
                        totalPaymentAmount
                    ) > 0.01 // Allow for small floating point differences
                  : !paymentMethod)
              }>
              {paymentStatus === "loading" ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing Payment...
                </>
              ) : (
                "Save Payment"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePaymentModal;
